import { Modal, Form } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { User, Shield, Users, Settings, Eye, Edit3, AlertTriangle, Lock } from 'lucide-react';

// Phase 1.2 (access-control redesign) note: every `roles` array referenced
// in this modal belongs to the *target* user being edited (passed in via
// props), NOT the caller. There is no caller-self role read to migrate to
// useCapabilities() here. If a future "is the CALLER allowed to perform
// this role-edit?" gate gets added, route it through useCapabilities()
// (isAdmin / isOwner) rather than re-introducing a self-fetch.
const RoleModal = ({ show, handleClose, user, roles, allRoles, handleSaveRoles, associatedUsers }) => {
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (user) {
            setSelectedRoles(user.roles);
            setHasChanges(false);
        }
    }, [user]);

    // Check if removing admin role would leave no admins
    const wouldRemoveLastAdmin = (rolesToUpdate) => {
        const currentAdmins = associatedUsers?.filter(u => u.roles.includes('Admin')) || [];
        const isCurrentUserAdmin = user?.roles.includes('Admin');
        const willBeAdmin = rolesToUpdate.includes('Admin');

        return isCurrentUserAdmin && !willBeAdmin && currentAdmins.length <= 1;
    };

    const handleRoleChange = (e) => {
        const roleName = e.target.name;
        let newRoles;

        if (e.target.checked) {
            newRoles = [...selectedRoles, roleName];
        } else {
            newRoles = selectedRoles.filter((role) => role !== roleName);
        }

        setSelectedRoles(newRoles);
        setHasChanges(JSON.stringify(newRoles.sort()) !== JSON.stringify(user.roles.sort()));
    };

    const handleSubmit = () => {
        if (wouldRemoveLastAdmin(selectedRoles)) {
            return; // Prevent submission
        }
        handleSaveRoles(user.user_id, selectedRoles);
        setHasChanges(false);
    };

    const getRoleIcon = (roleName) => {
        switch (roleName.toLowerCase()) {
            case 'admin':
                return <Shield size={18} className="text-danger" />;
            case 'manager':
                return <Users size={18} className="text-warning" />;
            case 'user':
                return <User size={18} className="text-info" />;
            case 'viewer':
                return <Eye size={18} className="text-secondary" />;
            default:
                return <Settings size={18} className="text-primary" />;
        }
    };

    const getRoleDescription = (roleName) => {
        switch (roleName.toLowerCase()) {
            case 'admin':
                return 'Full åtkomst till alla funktioner och inställningar';
            case 'manager':
                return 'Kan hantera team och projekt, begränsad systemåtkomst';
            case 'user':
                return 'Standard användarrättigheter för dagligt arbete';
            case 'viewer':
                return 'Endast läsrättigheter, kan inte göra ändringar';
            default:
                return 'Anpassad roll med specifika behörigheter';
        }
    };

    // Why a switch is locked, or null when it is not. Returning the reason
    // rather than a boolean means the row can say so instead of just looking
    // broken.
    const roleLockReason = (roleName) => {
        // Owner is a singleton with a dedicated transfer path (RoleService
        // §2.4): the role-update PUT refuses both to grant Owner to anyone but
        // the current Owner and to remove it from the sole Owner. Offering the
        // switch meant offering a call that can only come back 409.
        if (roleName.toLowerCase() === 'owner') {
            return selectedRoles.includes(roleName)
                ? 'Ägarskapet kan inte tas bort här — det flyttas via ägarbyte.'
                : 'Ägarskap ges via ägarbyte, inte genom att lägga till rollen här.';
        }
        // Never strand a company without an admin.
        if (roleName === 'Admin' && selectedRoles.includes('Admin')
            && wouldRemoveLastAdmin(selectedRoles.filter(r => r !== 'Admin'))) {
            return 'Företaget måste ha minst en administratör.';
        }
        return null;
    };


    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <div className="d-flex align-items-center">
                    <div className="user-avatar-modal me-3">
                        {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                    </div>
                    <div>
                        <Modal.Title className="mb-1 h4">
                            Hantera roller
                        </Modal.Title>
                        <p className="text-muted mb-0 small">
                            {user?.first_name} {user?.last_name} • {user?.email}
                        </p>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="px-4 py-3">
                {wouldRemoveLastAdmin(selectedRoles) && (
                    <div className="alert alert-warning d-flex align-items-center mb-4">
                        <AlertTriangle size={20} className="me-2" />
                        <div>
                            <strong>Varning:</strong> Du kan inte ta bort admin-rollen från den sista administratören.
                            Det måste finnas minst en admin i företaget.
                        </div>
                    </div>
                )}

                <div className="mb-3">
                    <h6 className="text-muted mb-3 text-uppercase small fw-bold">
                        Tillgängliga roller
                    </h6>
                </div>

                <Form>
                    <div className="role-pills">
                        {allRoles.map((role) => {
                            const isChecked = selectedRoles.includes(role.role_name);
                            const lockReason = roleLockReason(role.role_name);
                            const isDisabled = Boolean(lockReason);

                            return (
                                <div
                                    key={role.role_id}
                                    className={`role-item ${isChecked ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                    title={lockReason || getRoleDescription(role.role_name)}
                                    onClick={() => !isDisabled && handleRoleChange({
                                        target: { name: role.role_name, checked: !isChecked },
                                    })}
                                >
                                    <div className="role-header">
                                        <span className="role-icon">
                                            {getRoleIcon(role.role_name)}
                                        </span>
                                        <span className="role-name">{role.role_name}</span>
                                        {isDisabled && (
                                            <span className="role-locked">
                                                <Lock size={12} /> Låst
                                            </span>
                                        )}
                                        <Form.Check
                                            type="switch"
                                            id={`role-${role.role_name}`}
                                            name={role.role_name}
                                            checked={isChecked}
                                            onChange={handleRoleChange}
                                            disabled={isDisabled}
                                            className="role-switch"
                                        />
                                        <span className="role-description">
                                            {getRoleDescription(role.role_name)}
                                        </span>
                                        {lockReason && (
                                            <span className="role-lock-reason">{lockReason}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Form>

                {selectedRoles.length > 0 && (
                    <div className="mt-4 pt-3 border-top">
                        <h6 className="text-muted mb-2 small">Aktiva roller:</h6>
                        <div className="d-flex flex-wrap gap-2">
                            {selectedRoles.map((roleName, index) => (
                                <span key={index} className="badge bg-primary rounded-pill px-3 py-2">
                                    {getRoleIcon(roleName)}
                                    <span className="ms-2">{roleName}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0">
                <Button variant="outline-secondary" onClick={handleClose}>
                    Avbryt
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!hasChanges || wouldRemoveLastAdmin(selectedRoles)}
                    className="d-flex align-items-center gap-2"
                >
                    <Edit3 size={16} />
                    Spara ändringar
                </Button>
            </Modal.Footer>

            <style jsx>{`
                .user-avatar-modal {
                    width: 48px;
                    height: 48px;
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 600;
                    font-size: 1rem;
                }

                /* Roles as toggle pills, matching the invite modal. Eight
                   stacked 78px cards made this modal 1007px tall in a 756px
                   viewport, so assigning a role meant scrolling a dialog. */
                .role-pills {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }
                
                .role-pills .role-item {
                    display: inline-flex;
                    background: none;
                    border: 0;
                    border-radius: 999px;
                    cursor: default;
                }
                
                .role-pills .role-header {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.375rem;
                    padding: 0.3125rem 0.75rem;
                    border: 1px solid #d5d9e0;
                    border-radius: 999px;
                    background: #fff;
                    transition: border-color 0.15s ease, background-color 0.15s ease;
                }
                
                .role-pills .role-item:not(.disabled) .role-header {
                    cursor: pointer;
                }
                
                .role-pills .role-item:hover:not(.disabled):not(.selected) .role-header {
                    border-color: #b6bcc7;
                    background: #f9fafb;
                }
                
                .role-pills .role-item.selected .role-header {
                    border-color: var(--primary);
                    background: var(--rgba-primary-1);
                    color: var(--primary);
                }
                
                .role-pills .role-item.selected:hover:not(.disabled) .role-header {
                    background: var(--rgba-primary-2);
                }
                
                .role-pills .role-item.disabled {
                    opacity: 0.65;
                }
                
                .role-pills .role-icon {
                    display: inline-flex;
                    color: #6b7280;
                }
                
                .role-pills .role-item.selected .role-icon {
                    color: var(--primary);
                }
                
                .role-pills .role-name {
                    font-size: 0.8125rem;
                    font-weight: 500;
                    margin: 0;
                }
                
                .role-locked {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.125rem 0.4375rem;
                    font-size: 0.6875rem;
                    font-weight: 600;
                    color: #92400e;
                    background: #fef3c7;
                    border-radius: 999px;
                }
                
                /* The switch stays in the DOM (it is the control, and the tests
                   drive it) but the pill itself is the affordance. Description
                   and lock reason move to the tooltip and stay readable to
                   assistive tech. */
                .role-pills .role-switch,
                .role-pills .role-description,
                .role-pills .role-lock-reason {
                    position: absolute;
                    width: 1px;
                    height: 1px;
                    margin: -1px;
                    padding: 0;
                    overflow: hidden;
                    clip: rect(0 0 0 0);
                    white-space: nowrap;
                }

                .role-name {
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 0.25rem;
                }

                .role-description {
                    font-size: 0.875rem;
                    line-height: 1.4;
                }

                .role-switch .form-check-input {
                    width: 3rem;
                    height: 1.5rem;
                }

                .role-switch .form-check-input:checked {
                    background-color: var(--primary);
                    border-color: var(--primary);
                }

                .role-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .badge {
                    display: inline-flex;
                    align-items: center;
                    font-size: 0.875rem;
                    font-weight: 500;
                }

                .modal-header {
                    padding: 1.5rem 1.5rem 0 1.5rem;
                }

                .modal-body {
                    padding: 1.5rem;
                }

                .modal-footer {
                    padding: 0 1.5rem 1.5rem 1.5rem;
                }
            `}</style>
        </Modal>
    );
};

export default RoleModal;


// import { Modal, Form } from 'react-bootstrap';
// import { Button } from 'react-bootstrap';
// import { useState, useEffect } from 'react';


// // ...

// const RoleModal = ({ show, handleClose, user, roles, allRoles, handleSaveRoles }) => {
//     const [selectedRoles, setSelectedRoles] = useState([]);

//     useEffect(() => {
//         if (user) {
//             setSelectedRoles(user.roles);
//         }
//     }, [user]);

//     const handleRoleChange = (e) => {
//         const roleName = e.target.name;
//         if (e.target.checked) {
//             setSelectedRoles([...selectedRoles, roleName]);
//         } else {
//             setSelectedRoles(selectedRoles.filter((role) => role !== roleName));
//         }
//     };

//     const handleSubmit = () => {
//         handleSaveRoles(user.user_id, selectedRoles);
//     };

//     return (
//         <Modal show={show} onHide={handleClose}>
//             <Modal.Header closeButton>
//                 <Modal.Title>Ändra roller för {user?.first_name} {user?.last_name}</Modal.Title>
//             </Modal.Header>
//             <Modal.Body>
//                 <Form>
//                     {allRoles.map((role) => (
//                         <Form.Group controlId={`role-${role.role_name}`} key={role.role_id}>
//                             <Form.Check
//                                 type="checkbox"
//                                 label={role.role_name}
//                                 name={role.role_name}
//                                 checked={selectedRoles.includes(role.role_name)}
//                                 onChange={handleRoleChange}
//                             />
//                         </Form.Group>
//                     ))}
//                 </Form>
//             </Modal.Body>
//             <Modal.Footer>
//                 <Button variant="secondary" onClick={handleClose}>Avbryt</Button>
//                 <Button variant="primary" onClick={handleSubmit}>Spara</Button>
//             </Modal.Footer>
//         </Modal>
//     );
// };

// export default RoleModal;